import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard,
  Shield,
  Search,
  ShoppingBag,
  Share2,
  GitGraph,
  Activity,
  Database,
  Globe,
  Network,
  Wifi,
  Radio,
  ScanLine,
  AppWindow as Window,
  ShieldAlert,
  Key,
  FileWarning,
  Bell,
  Users,
  Settings,
  ChevronDown,
  ChevronRight,
  Radar,
  Rss,
  Crosshair,
  Target,
  Mail,
  FileText,
  AlertOctagon,
  BookOpen,
  Boxes,
  LineChart,
  Webhook,
  Zap,
  RssIcon,
  RussianRuble,
  NewspaperIcon,
  UserCog
} from 'lucide-react';

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
  indent?: boolean;
  isActive?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon: Icon, label, indent = false, isActive = false }) => (
  <Link
    to={to}
    className={`flex items-center space-x-3 py-1.5 px-3 rounded-lg text-sm transition-colors ${
      indent ? 'ml-6' : ''
    } ${
      isActive
        ? 'bg-[#1A1F2E] text-white'
        : 'text-gray-400 hover:text-white hover:bg-[#1A1F2E]'
    }`}
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span className="truncate">{label}</span>
  </Link>
);

interface NavSectionProps {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  containsActivePath: boolean;
}

const NavSection: React.FC<NavSectionProps> = ({ 
  icon: Icon, 
  label, 
  children, 
  defaultExpanded = false,
  containsActivePath
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || containsActivePath);
  
  useEffect(() => {
    if (containsActivePath) {
      setIsExpanded(true);
    }
  }, [containsActivePath]);

  return (
    <div className="space-y-0.5">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1.5 px-3 text-gray-400 hover:text-white rounded-lg transition-colors group"
      >
        <div className="flex items-center space-x-3">
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-medium truncate">{label}</span>
        </div>
        <div className="transform transition-transform duration-200">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 opacity-50 group-hover:opacity-100" />
          ) : (
            <ChevronRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="space-y-0.5 animate-in slide-in-from-left-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

const Sidebar = () => {
  const location = useLocation();
  
  const isPathActive = (path: string) => location.pathname === path;
  const doesSectionContainActivePath = (paths: string[]) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F1629] flex flex-col">
      <div className="px-4 h-16 flex items-center flex-shrink-0 border-b border-gray-800/50">
        <Link to="/" className="flex items-center space-x-3">
          <img src="/logo icon white.svg" alt="Flawtrack Logo" className="h-8 w-8" />
          <span className="text-xl font-bold text-white">Flawtrack</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent hover:scrollbar-thumb-gray-700">
        {/* Overview */}
        <NavItem
          to="/"
          icon={LayoutDashboard}
          label="Dashboard"
          isActive={isPathActive('/')}
        />

        {/* Asset Management */}
        <NavSection 
          icon={Database} 
          label="Asset Management" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/domains', '/subdomains', '/ip-addresses', '/ports', '/web-applications'])}
        >
          {/* Asset Management 
          <NavItem to="/assets" icon={Database} label="All Assets" indent />
          */}

          <NavItem to="/domains" icon={Globe} label="Domains" indent isActive={isPathActive('/domains')} />
          <NavItem to="/subdomains" icon={Network} label="Subdomains" indent isActive={isPathActive('/subdomains')} />
          <NavItem to="/ip-addresses" icon={Wifi} label="IP Addresses" indent isActive={isPathActive('/ip-addresses')} />
          <NavItem to="/ports" icon={Radio} label="Ports" indent isActive={isPathActive('/ports')} />
          <NavItem to="/web-applications" icon={Window} label="Web Applications" indent isActive={isPathActive('/web-applications')} />

          {/* Asset Management
          <NavItem to="/attack-surface" icon={GitGraph} label="Attack Surface Graph" indent />
          */}

        </NavSection>

        {/* Security Operations */}
        <NavSection 
          icon={ShieldAlert} 
          label="Security Operations" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/findings', '/security-center', '/incidents'])}
        >
          {/* <NavItem to="/monitoring" icon={Activity} label="Security Monitoring" indent /> */}
          <NavItem to="/findings" icon={ShieldAlert} label="Security Findings" indent isActive={isPathActive('/findings')} />
          {/* <NavItem to="/domain-security" icon={Globe} label="Domain Security" indent />
          <NavItem to="/email-security" icon={Mail} label="Email Security" indent /> */}
          <NavItem to="/security-center" icon={Shield} label="Security Center" indent isActive={isPathActive('/security-center')} />
                   {/* <NavItem to="/domain-security" icon={Globe} label="Domain Security" indent />

          <NavItem to="/incidents" icon={AlertOctagon} label="Incident Response" indent isActive={isPathActive('/incidents')} />
        */}
          </NavSection>

        {/* Brand Protection */}
        <NavSection 
          icon={Shield} 
          label="Brand Protection" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/brand-protection', '/vip-profiles'])}
        >
          {/* <NavItem to="/brand-protection" icon={Shield} label="Overview" indent /> */}
          {/* <NavItem to="/brand-protection/impersonation" icon={Target} label="Brand Impersonation" indent /> */}
          <NavItem to="/brand-protection/domain-monitoring" icon={Globe} label="Domain Monitoring" indent isActive={isPathActive('/brand-protection/domain-monitoring')} />
          <NavItem to="/brand-protection/infringements" icon={AlertOctagon} label="Infringements" indent isActive={isPathActive('/brand-protection/infringements')} />
          <NavItem to="/vip-profiles" icon={UserCog} label="VIP Profiles" indent isActive={isPathActive('/vip-profiles')} />

          {/* <NavItem to="/brand-protection/trademark" icon={FileText} label="Trademark Monitoring" indent /> */}
          {/* <NavItem to="/brand-protection/marketplace" icon={ShoppingBag} label="Marketplace Monitoring" indent /> */}
          {/* <NavItem to="/brand-protection/social-media" icon={Share2} label="Social Media Monitoring" indent /> */}
        </NavSection>

        {/* Threat Intelligence */}
        {/* <NavSection icon={Radar} label="Threat Intelligence" defaultExpanded>
          <NavItem to="/threat-intelligence" icon={Radar} label="Overview" indent />
          <NavItem to="/threat-intelligence/cve-dashboard" icon={ShieldAlert} label="CVEs Dashboard" indent />
          <NavItem to="/threat-intelligence/cve" icon={Shield} label="CVEs List" indent />
          <NavItem to="/threat-intelligence/threat-feeds-dashboard" icon={NewspaperIcon} label="Threat Feeds Dashboard" indent />
          <NavItem to="/threat-intelligence/threat-feeds" icon={Rss} label="Threat Feeds" indent />
        </NavSection> */}

        {/* Data Protection */}
        <NavSection 
          icon={Activity} 
          label="Data Monitoring" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/data-protection', '/alerts'])}
        >
          <NavItem to="/data-protection" icon={FileWarning} label="Overview" indent isActive={isPathActive('/data-protection')} />
          <NavItem to="/data-protection/search" icon={Search} label="Search" indent isActive={isPathActive('/data-protection/search')} />
          <NavItem to="/data-protection/public-leaks" icon={Key} label="Public Leaks" indent isActive={isPathActive('/data-protection/public-leaks')} />
          <NavItem to="/data-protection/stealer-leaks" icon={Key} label="Stealer Leaks" indent isActive={isPathActive('/data-protection/stealer-leaks')} />
          <NavItem to="/data-protection/data-leaks" icon={FileWarning} label="Data Leaks" indent isActive={isPathActive('/data-protection/data-leaks')} />
        </NavSection>

        {/* Compliance & Reports */}
        <NavSection 
          icon={FileText} 
          label="Compliance & Reports" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/compliance', '/reports'])}
        >
          {/* <NavItem to="/compliance" icon={Shield} label="Overview" indent isActive={isPathActive('/compliance')} /> */}
          <NavItem to="/compliance/checklist" icon={FileText} label="NACSA Checklist" indent isActive={isPathActive('/compliance/checklist')} />
          <NavItem to="/reports" icon={Activity} label="Reports" indent isActive={isPathActive('/reports')} />
        </NavSection>

        {/* Documentation */}
        {/* <NavSection 
          icon={BookOpen} 
          label="Documentation" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/api-docs'])}
        >
          <NavItem to="/api-docs" icon={Webhook} label="API Documentation" indent isActive={isPathActive('/api-docs')} />
        </NavSection> */}

        {/* Scan Settings */}
        {/* <NavSection 
          icon={ScanLine} 
          label="Scan Settings" 
          defaultExpanded={false}
          containsActivePath={doesSectionContainActivePath(['/scan-settings'])}
        >
        </NavSection> */}

        {/* Settings */}
        <div className="pt-2 mt-2 border-t border-gray-800/50">
          <NavItem to="/scan-settings" icon={ScanLine} label="Scans" isActive={isPathActive('/scan-settings')} />
          <NavItem to="/settings" icon={Settings} label="Settings" isActive={isPathActive('/settings')} />
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;